import { useCallback, useEffect, useRef, useState } from "react";
import { useUnsavedChangesStore } from "@/stores/unsavedChangesStore";

/**
 * Register a dirty flag under a stable key. Auto-clears on unmount.
 *
 * @param key   Unique registry key (e.g. `weight-energy-target:${appointmentId}`).
 * @param isDirty  Whether this section currently has unsaved changes.
 * @param label  Optional friendly label rendered in the unsaved-changes dialog.
 *               Falls back to a derived title-case of the key prefix.
 */
export const useDirtyFlag = (key: string, isDirty: boolean, label?: string) => {
  const setDirty = useUnsavedChangesStore((s) => s.setDirty);
  useEffect(() => {
    setDirty(key, isDirty, label);
  }, [key, isDirty, label, setDirty]);
  useEffect(() => {
    return () => setDirty(key, false);
  }, [key, setDirty]);
};

/**
 * Returns a function that runs `action` immediately when nothing is dirty,
 * or opens the custom unsaved-changes dialog when there are pending edits.
 * The action runs only if the user clicks "Discard and continue".
 *
 * Replaces the old sync `window.confirm`-based API.
 */
export const useConfirmUnsaved = () => {
  const requestConfirm = useUnsavedChangesStore((s) => s.requestConfirm);
  return useCallback(
    (action: () => void) => requestConfirm(action),
    [requestConfirm]
  );
};

/**
 * Returns `true` for ~1.5s each time the user clicks "Stay on this page"
 * AND the caller is currently dirty. Use to apply a temporary visual
 * highlight on dirty inputs so they're easy to spot.
 */
export const useDirtyHighlight = (isDirty: boolean) => {
  const nonce = useUnsavedChangesStore((s) => s.highlightNonce);
  const [highlighted, setHighlighted] = useState(false);
  const lastNonce = useRef(nonce);

  useEffect(() => {
    if (nonce === lastNonce.current) return;
    lastNonce.current = nonce;
    if (!isDirty) return;
    setHighlighted(true);
    const t = setTimeout(() => setHighlighted(false), 1500);
    return () => clearTimeout(t);
  }, [nonce, isDirty]);

  return highlighted;
};

/**
 * Mount once — shows the browser's native "leave site?" prompt while any key
 * is dirty. This is the only place we still use a system dialog because the
 * `beforeunload` event cannot be intercepted with a custom UI.
 */
export const useBeforeUnloadGuard = () => {
  const hasDirty = useUnsavedChangesStore(
    (s) => Object.keys(s.dirtyKeys).length > 0
  );
  useEffect(() => {
    if (!hasDirty) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [hasDirty]);
};
